import { Form, FormLayout, Modal, TextField, Text, Box, VerticalStack } from '@shopify/polaris';
import { useContext, useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { putLead, getLeadCenters } from '../../../services/leadsService';
import { getProductCategoriesByGender } from '../../../services/productService';
import { buildCategoryOptions } from '../../../helpers/leadCategoryOptions';
import { togglesToGender, genderToToggles } from '../../../helpers/leadGender';
import { SET_OK, SET_ERROR } from '../../../context/ToastReducer';
import ToastContext from '../../../context/ToastContext';
import { LeadPhoneField, LeadMultiAutocomplete, LeadGenderChips } from './LeadFields';

export const EditLeadModal = (props) => {
  const { lead, active, id, onSave, onClose } = props;
  const [, dispatchToast] = useContext(ToastContext);
  const [saving, setSaving] = useState(false);

  // Normalizamos a strings/'' para que los controles de Polaris no reciban
  // null. Sólo campos de persona (el género, las categorías y los centros se
  // gestionan en su propio estado; el status no se toca desde aquí).
  const [dataForm, setDataForm] = useState({
    name: lead.name || '',
    surname: lead.surname || '',
    email: lead.email || '',
    phone: lead.phone || '',
    source: lead.source || '',
    notes: lead.notes || '',
  });

  const initialGender = genderToToggles(lead.interestGender);
  const [caballero, setCaballero] = useState(initialGender.caballero);
  const [dama, setDama] = useState(initialGender.dama);
  const [selectedCategories, setSelectedCategories] = useState(
    lead.interestCategory ? lead.interestCategory.split(':').filter(Boolean) : [],
  );
  // Centros asociados, precargados desde lead.deposits (relación M:N hidratada).
  const [selectedCenters, setSelectedCenters] = useState(
    (lead.deposits || []).map((d) => String(d.depositId)),
  );

  const setField = (field) => (value) => setDataForm((prev) => ({ ...prev, [field]: value }));

  // Centros disponibles (solo se cargan con el modal abierto), cacheados.
  const { data: centers = [], isLoading: loadingCenters } = useQuery(
    ['leadCenters'],
    () => getLeadCenters().then(({ data }) => data.centers || []),
    {
      enabled: active,
      staleTime: 1000 * 60 * 60,
      onError: (e) =>
        dispatchToast({
          type: SET_ERROR,
          payload: e.response?.data || { message: 'No se pudieron cargar los centros' },
        }),
    },
  );

  const centerOptions = useMemo(
    () => centers.map((c) => ({ value: String(c.id), label: `${c.name} (${c.code})` })),
    [centers],
  );

  // Categorías de catálogo por género (Hombre/Mujer), cacheadas.
  const { data: catsHombre = [], isLoading: loadingHombre } = useQuery(
    ['leadCategories', 'Hombre'],
    () => getProductCategoriesByGender('Hombre'),
    { enabled: active, staleTime: 1000 * 60 * 60 },
  );
  const { data: catsMujer = [], isLoading: loadingMujer } = useQuery(
    ['leadCategories', 'Mujer'],
    () => getProductCategoriesByGender('Mujer'),
    { enabled: active, staleTime: 1000 * 60 * 60 },
  );

  const categoryOptions = useMemo(
    () => buildCategoryOptions(caballero, dama, catsHombre, catsMujer),
    [caballero, dama, catsHombre, catsMujer],
  );

  // Al cambiar el género, descartamos categorías que dejen de estar disponibles.
  // No corremos hasta que las categorías estén cargadas (para no vaciar las
  // preseleccionadas mientras llega la red).
  useEffect(() => {
    if (loadingHombre || loadingMujer) return;
    const valid = new Set(categoryOptions.map((o) => o.value));
    setSelectedCategories((prev) => prev.filter((c) => valid.has(c)));
  }, [categoryOptions, loadingHombre, loadingMujer]);

  // Una sola marcada → ese género; ambas o ninguna → null (sin preferencia = ambos).
  const interestGender = useMemo(() => togglesToGender(caballero, dama), [caballero, dama]);

  const handleSubmit = () => {
    if (!dataForm.email.trim() && !dataForm.phone.trim()) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'Indica al menos un email o un teléfono' } });
      return;
    }
    if (selectedCenters.length === 0) {
      dispatchToast({ type: SET_ERROR, payload: { message: 'Selecciona al menos un centro' } });
      return;
    }

    const payload = {
      name: dataForm.name.trim(),
      surname: dataForm.surname.trim() || null,
      email: dataForm.email.trim() || null,
      phone: dataForm.phone.trim() || null,
      interestGender,
      interestCategory: selectedCategories.length ? selectedCategories.join(':') : null,
      source: dataForm.source.trim() || null,
      notes: dataForm.notes.trim() || null,
      depositIds: selectedCenters.map(Number),
    };

    setSaving(true);
    putLead(id, payload)
      .then((r) => {
        dispatchToast({ type: SET_OK, payload: r.data });
        // El back devuelve el lead actualizado e hidratado con deposits, así la
        // ficha refresca sus centros (no usamos el payload local, que no los trae).
        onSave(r.data);
        onClose();
      })
      .catch((e) => {
        dispatchToast({ type: SET_ERROR, payload: e.response.data });
      })
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      open={active}
      onClose={() => onClose()}
      title="Editar contacto"
      large
      primaryAction={{ content: 'Guardar cambios', onAction: handleSubmit, loading: saving }}
      secondaryActions={[{ content: 'Cancelar', onAction: () => onClose() }]}
    >
      <Modal.Section>
        <Form onSubmit={handleSubmit}>
          <VerticalStack gap="5">
            <Box>
              <Box paddingBlockEnd="2">
                <Text variant="headingSm" as="h3">
                  Identificación
                </Text>
              </Box>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label="Nombre"
                    value={dataForm.name}
                    onChange={setField('name')}
                    autoComplete="off"
                    requiredIndicator
                  />
                  <TextField
                    label="Apellidos"
                    value={dataForm.surname}
                    onChange={setField('surname')}
                    autoComplete="off"
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <TextField
                    type="email"
                    label="Email"
                    value={dataForm.email}
                    onChange={setField('email')}
                    autoComplete="off"
                  />
                  <LeadPhoneField value={dataForm.phone} onChange={setField('phone')} />
                </FormLayout.Group>
                <Text variant="bodySm" color="subdued" as="p">
                  Es obligatorio indicar al menos un email o un teléfono.
                </Text>
              </FormLayout>
            </Box>

            <Box>
              <Box paddingBlockEnd="2">
                <Text variant="headingSm" as="h3">
                  Interés
                </Text>
              </Box>
              <VerticalStack gap="4">
                <LeadGenderChips
                  caballero={caballero}
                  dama={dama}
                  onToggleCaballero={() => setCaballero((v) => !v)}
                  onToggleDama={() => setDama((v) => !v)}
                />
                <FormLayout>
                  <FormLayout.Group>
                    <LeadMultiAutocomplete
                      label="Categorías de interés"
                      placeholder="Busca y selecciona categorías"
                      options={categoryOptions}
                      selected={selectedCategories}
                      onChange={setSelectedCategories}
                      loading={loadingHombre || loadingMujer}
                      helpText="Cada categoría lleva su género; las compartidas salen una vez por género."
                      emptyMessage="No hay categorías que coincidan"
                    />
                    <LeadMultiAutocomplete
                      label="Centros asociados (al menos uno)"
                      placeholder="Busca y selecciona centros"
                      options={centerOptions}
                      selected={selectedCenters}
                      onChange={setSelectedCenters}
                      loading={loadingCenters}
                      emptyMessage="No hay centros disponibles"
                    />
                  </FormLayout.Group>
                </FormLayout>
              </VerticalStack>
            </Box>

            <Box>
              <Box paddingBlockEnd="2">
                <Text variant="headingSm" as="h3">
                  Origen y observaciones
                </Text>
              </Box>
              <FormLayout>
                <TextField
                  label="¿Cómo nos conoció?"
                  value={dataForm.source}
                  onChange={setField('source')}
                  autoComplete="off"
                  placeholder="Tienda, web, evento…"
                />
                <TextField
                  label="Observaciones"
                  value={dataForm.notes}
                  onChange={setField('notes')}
                  autoComplete="off"
                  multiline={3}
                />
              </FormLayout>
            </Box>
          </VerticalStack>
        </Form>
      </Modal.Section>
    </Modal>
  );
};
